/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { CommentAuthorRole, FeedbackThreadStatus } from '@app/app/page-modules/course/models/course-assignments.model';

/**
 * Neutral render contract for a single feedback comment.
 *
 * Both drivers map onto this: the learner surfaces from `FeedbackCommentDetails`
 * (`portfolio-feedback.util.ts`) and the reviewer marking view from `MarkingCommentVM`
 * (`marking-editor.model.ts`). Nothing here is fetched or mutated by the renderers — the
 * components that consume it are purely presentational.
 */
export interface FeedbackCommentItem {
  id: string;
  /** Compared against the viewer's id to gate edit/delete to the author. */
  authorId: string | null;
  authorName: string | null;
  /** Tints the avatar so reviewer feedback is distinguishable at a glance; null when unknown. */
  authorRole: CommentAuthorRole | null;
  body: string;
  createdAt: string;
  edited: boolean;
  /** Renders an "in reply to X" hint only — threads are flat, never indented trees. */
  parentCommentId: string | null;
  /** The submission cycle this comment belongs to; absent for drivers without cycles (peer review). */
  cycle?: number;
  /** A marker's comment the learner has not been sent yet. */
  draft?: boolean;
  /** A PDF mark's own text. Editing or deleting it changes the mark. */
  annotationNote?: boolean;
}

/** Neutral render contract for one backend-owned feedback thread (OPEN / RESOLVED). */
export interface FeedbackThreadItem {
  id: string;
  status: FeedbackThreadStatus;
  /**
   * Optional caption above the thread, already translated. Omitted where the thread renders directly
   * under the item it is anchored to (the anchor is then obvious); set in page-level blocks where
   * several anchors' threads are listed together. On a PDF thread it is the file name, then where in
   * the file the mark is, joined by `PDF_THREAD_LABEL_SEPARATOR`.
   */
  label?: string | null;
  resolvedAt?: string | null;
  /** Display name, already anonymity-masked; null ⇒ unresolved or masked — render no name. */
  resolvedByName?: string | null;
  /** Distinct cycles of the visible comments — label metadata only ("Cycle N" badges). */
  cycles: number[];
  commentCount: number;
  /** Oldest first. */
  comments: FeedbackCommentItem[];
  /** Set on a thread about a mark on a PDF. */
  pdfAnnotation?: PdfAnnotationRef | null;
  /** Read from the PDF itself rather than stored as feedback (playlists): it takes no replies and can't be resolved. */
  readOnly?: boolean;
}

/** Joins the parts of a PDF thread's label. The list cuts a long file name short at the first one. */
export const PDF_THREAD_LABEL_SEPARATOR = ' · ';

/** The mark a PDF comment thread is about. */
export interface PdfAnnotationRef {
  /** The file's stored path (`FileAnswer.path`). */
  file: string;
  annotationId: string;
  /** Null once the mark is removed. */
  pageIndex: number | null;
  /** The submission cycle the file was annotated in (assignments). */
  cycle?: number;
  /** The mark was deleted. Its thread outlives it while replies remain, with nothing to show on the PDF. */
  removed?: boolean;
  /** The mark is in a submission this view doesn't show, so there is nothing to jump to. */
  notOnShow?: boolean;
  /** The mark is a text box on show: deleting its text deletes it. */
  textBox?: boolean;
}

/**
 * A reply the viewer asked to post. The composer clears itself on submit so the box doesn't sit there
 * looking unsent, so the driver must call `failed` when the request doesn't land — otherwise the text
 * is gone with only a toast to show for it.
 */
export interface FeedbackReplyEvent {
  threadId: string;
  body: string;
  /** Send to the learner at once rather than keep as a draft; see `FeedbackSubmission`. */
  sendNow: boolean;
  /** Hands the text back to the composer it came from. Safe to call after the thread list is gone. */
  failed: () => void;
}

/** What a composer posts. `sendNow` is only ever true where the composer offered "Send now". */
export interface FeedbackSubmission {
  body: string;
  sendNow: boolean;
}

/**
 * Scrolls to the first unresolved thread rendered on the page. Threads mark themselves with
 * `data-thread-status` for exactly this; it replaces the deleted feedback drawer's `scrollTo()`,
 * which had to hunt for an anchor key inside its own overlay.
 *
 * Falls back to the page-level feedback panel when no open thread is rendered — a thread can be
 * hidden inside a collapsed section, and scrolling to the feedback that *is* on the page beats the
 * button doing nothing at all.
 */
export function focusFirstOpenThread(root: ParentNode = document): void {
  const target = root.querySelector('[data-thread-status="OPEN"]') ?? root.querySelector('[data-feedback-panel]');
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/** Two-letter initials for the avatar chip; `?` when there is no usable name. */
export function commentInitials(name: string | null | undefined): string {
  return (
    (name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('') || '?'
  );
}

/** How many OPEN threads a single anchor may carry before the "add a comment" button is hidden. */
export const MAX_OPEN_THREADS_PER_ANCHOR = 1;
